import { Injectable } from '@angular/core';
import { AppHelperService } from 'modules/app/services/app-helper.service';
import { ProductTocService } from './product-toc.service';

@Injectable()
export class ContentAccessHelperService {

    private classStartDate: number;
    private classEndDate: number;
    private currentTime: number;
    private activeAssignmentDetails: any;
    private scheduledAssignmentDetails: any;
    private isContentAccessEnabled = false;
    /**
     * Map of lock rules for items (of an assignment-only product)
     * It stores the default lock rule for an item, which is the minimum end time of
     * the unlock rules and assignments for the item.
     * 
     * - If an item is not found in this map, it is considered to be always locked.
     */
    private lockRulesForAssignOnlyItems: any = {};

    constructor(
        private appHelperService: AppHelperService,
        private productTocService: ProductTocService
      ) {}

    setContentAccessEnabled(isContentAccessEnabled) {
        this.isContentAccessEnabled = isContentAccessEnabled;
    }

    getContentAccessEnabled() {
        return this.isContentAccessEnabled;
    }

    setClassStartDate(classStartDate) {
        this.classStartDate = classStartDate;
    }

    setClassEndDate(classEndDate) {
        this.classEndDate = classEndDate;
    }

    convertTime(timestamp) {
        const date = new Date(timestamp);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    setCurrentTime(currentTime) {
        this.currentTime = this.convertTime(currentTime).getTime();
    }

    getCurrentTime() {
        return this.currentTime;
    }

    setAssignmentDetails(activeAssignmentDetails, scheduledAssignmentDetails) {
        this.activeAssignmentDetails = activeAssignmentDetails;
        this.scheduledAssignmentDetails = scheduledAssignmentDetails;
    }

    /**
     * Updates the end time of the default lock rule set in case of assignment-only product
     * @param itemCode - The item code of the activity
     * @param newEndTime - The new end time of the default lock rule
     */
    updateAssignOnlyItemLockRule(itemCode, newEndTime) { 
        if (!this.lockRulesForAssignOnlyItems?.[itemCode]) {
            this.lockRulesForAssignOnlyItems[itemCode] = {
                startTime: this.classStartDate,
                endTime: newEndTime,
            };
            return;
        }

        if (this.lockRulesForAssignOnlyItems[itemCode]?.endTime > newEndTime) {
            this.lockRulesForAssignOnlyItems[itemCode].endTime = newEndTime;
        }
    }

    /**
     * Returns item state i.e., locked true/false and upcoming unlocked details if locked
     * 
     * @param rules array of lock rules e.g [{access: "lock", startTime, endTime }]
     * @param itemCode - The item code of the activity
     * @param isAssignmentOnlyProduct - Boolean flag to check if the product is assignment-only product
     * @returns {object} {isLocked: true/false, unlockRuleDetails: {startTime, endTime}}
     */
    getCurrentActiveRule(rules, itemCode, isAssignmentOnlyProduct = false) {
        const activeRule = {
            isLocked: false,
            unlockRuleDetails: undefined
        };

        // Check if the item is always locked for an assignment-only product
        if (isAssignmentOnlyProduct && rules.length === 1 && rules[0].startTime === this.classStartDate && rules[0].endTime === this.classEndDate) {
            activeRule.isLocked = true;
            return activeRule;
        }

        for(let i = 0; i < rules.length; i++) {
            if (this.currentTime >= rules[i].startTime && this.currentTime <= rules[i].endTime) {
                activeRule.isLocked = !this.isAssignmentActive(itemCode);
                if(activeRule.isLocked)
                    activeRule.unlockRuleDetails = {
                        startTime: rules[i].endTime + 1,
                        endTime: i == rules.length - 1 ? this.classEndDate : rules[i+1].startTime - 1,
                        access: 'unlock'
                    }
                break;
            } else if (this.currentTime < rules[i].startTime) break;
        }
        return activeRule;
    }

    /**
     * Returns filtered lock rules which lie between start date of class and end date of a class
     * 
     * @param rules array of lock rules e.g [{access: "lock", startTime, endTime }]
     * @returns rules array of lock rules e.g [{access: "lock", startTime, endTime }]
     */
    filterLockUnlockRules(rules) {
        let filteredRules = [];
        let first = 0, last = rules.length - 1;

        while(first < rules.length && rules[first].endTime < this.classStartDate)
            first++;

        if(first < rules.length)
            rules[first].startTime = Math.max(this.classStartDate, rules[first].startTime);

        while(last >= 0 && rules[last].startTime > this.classEndDate)
            last--;

        if(last >= 0)
            rules[last].endTime = Math.min(this.classEndDate, rules[last].endTime);

        while(first <= last)
            filteredRules.push(rules[first++]);

        return filteredRules;
    }

    /**
     * Returns if assignment is active for a activity or not
     * 
     * @param itemCode item code of activity
     * @returns {boolean} true/false based on dueDate and startDate of assignment
     */
    isAssignmentActive(itemCode) {
        return this.activeAssignmentDetails?.[itemCode]?.dueDate >= this.currentTime && this.activeAssignmentDetails?.[itemCode]?.startDate <= this.currentTime;
    }

    collectLockUnlockRules(itemCode, itemType, productCode, accessRules, isAssignmentOnlyProduct = false, parentAccessRules = { lockRules: [], unlockRules: [] }) {
        const rules = { lockRules: [], unlockRules: [] };

        if (accessRules && accessRules[itemCode]) {
            for (const rule in accessRules[itemCode]) {
                // Populate rulesData from rulesMeta (if startTime & endTime are not present then assign class startDate and endDate)
                accessRules[itemCode][rule].startTime = accessRules.rulesMeta[rule].startTime || this.classStartDate;
                accessRules[itemCode][rule].endTime = accessRules.rulesMeta[rule].endTime || this.classEndDate;
                accessRules[itemCode][rule].access = accessRules.rulesMeta[rule].access;

                if (accessRules[itemCode][rule].access === 'lock') {
                    rules.lockRules.push({...accessRules[itemCode][rule]});
                } else if (accessRules[itemCode][rule].access === 'unlock') {
                    rules.unlockRules.push({ ...accessRules[itemCode][rule] });
                    
                    // For an assignment-only product, update the default lock rule details.
                    if (isAssignmentOnlyProduct) {
                        this.updateAssignOnlyItemLockRule(itemCode, accessRules[itemCode][rule].startTime);
                    }
                }
            }
        }

        // Check for scheduled assignment and add unlock rule details
        if (this.scheduledAssignmentDetails?.[itemCode]) {
            rules.unlockRules.push({
                startTime: this.scheduledAssignmentDetails[itemCode].startDate,
                endTime: this.scheduledAssignmentDetails[itemCode].dueDate,
                access: 'unlock'
            });
        }

        // For an activity of assignment-only product, create and push a default lock rule
        if (isAssignmentOnlyProduct && itemType === 'resource') {
            const defaultLockRule = this.createDefaultLockRule(itemCode, productCode);
            rules.lockRules.push(defaultLockRule);
        }

        // add Parent level rules to the rules object if lock/unlock rules are present
        rules.lockRules = rules.lockRules.concat(parentAccessRules.lockRules);
        rules.unlockRules = rules.unlockRules.concat(parentAccessRules.unlockRules);
        
        return rules;
    }

    getFinalResultantLockDetails(rules) {
        let finalResultantLockDetails = [];
        
        if (rules.length === 0) return finalResultantLockDetails;

        let previousRule = rules[0];
        for(let i = 1; i < rules.length; i++) {
            if (previousRule.access === rules[i].access && previousRule.endTime + 1 === rules[i].startTime) {
                // Merge if previous(1 - 10) and current(11 - 15)
                previousRule.endTime = rules[i].endTime;
            } 
            // no overlapping
            else if (previousRule.endTime < rules[i].startTime) {
                if (previousRule.access === 'lock') finalResultantLockDetails.push(previousRule);
                previousRule = rules[i];
            } else {
                // overlapping
                if (previousRule.access === rules[i].access) {
                    // Merge if previous(1 - 10) and current(7,8,9,10 - x)
                    previousRule.endTime = Math.max(previousRule.endTime, rules[i].endTime);
                } else if (previousRule.access === 'lock') {
                    // modify the previous lock rule
                    if (previousRule.endTime <= rules[i].endTime) {
                        // terminate the previous lock rule as soon as the current unlock rule starts
                        previousRule.endTime = Math.min(rules[i].startTime - 1, previousRule.endTime);
                        finalResultantLockDetails.push(previousRule);
                        previousRule = rules[i];
                    } else {
                        // split the previous lock rule
                        let temp = {startTime: previousRule.startTime, endTime: previousRule.endTime, access: previousRule.access};
                        previousRule.endTime = rules[i].startTime - 1;
                        finalResultantLockDetails.push(previousRule);
                        previousRule = rules[i];
                        temp.startTime = rules[i].endTime + 1;

                        while(i < rules.length - 1 && previousRule.access === 'unlock') {
                            // Merge the upcoming/consecutive unlock rules
                            const next = rules[i+1];
                            if(next.access === 'unlock' && previousRule.endTime + 1 >= next.startTime) {
                                previousRule.endTime = Math.max(next.endTime, previousRule.endTime);
                                i++;
                            } else {
                                break;
                            }
                        }
                        // insert remaining part of previous lock rule
                        rules.splice(i + 1, 0, temp);
                    }
                } else if (previousRule.endTime <= rules[i].endTime) {
                    // Delay the current lock rule(7 - 20 Lock) start time only if its end time is greater than previous unlock rule(1 - 15 Unlock)
                    rules[i].startTime = previousRule.endTime + 1;
                    previousRule = rules[i];
                } 
            }
        }
        
        if (previousRule.access === 'lock') finalResultantLockDetails.push(previousRule);

        return finalResultantLockDetails;
    }

    mergeLockUnlockRules(rulesObject = { lockRules:[], unlockRules: [] }) {
        return [].concat(rulesObject.lockRules, rulesObject.unlockRules).sort((rule1, rule2) => rule1.startTime - rule2.startTime);
    }

    getRulesConflictItems(tocArray, tocFolderDepth, { isAssignmentCheck = false, itemsAlreadyAssigned = {}} = {}) {
        const unitConflictsMap = {};
        const lessonConflictsMap = {};
        let showWarning = false;
        if(tocFolderDepth === 0) {
            showWarning = tocArray.some(item => isAssignmentCheck ? item.isChecked && itemsAlreadyAssigned[item['item-code']] : item.isChecked && item.rulesList.length);
        } else {
            tocArray.forEach((unit, unitIndex) => {
                const unitLessons = this.productTocService.getLastFolderData(unit);
                unitLessons.forEach((lesson, lessonIndex) => {
                    lesson.items.some(item => {
                        const isConflict = isAssignmentCheck ? item.isChecked && itemsAlreadyAssigned[item['item-code']] : item.isChecked && item.rulesList.length;
                        if(isConflict) {
                            if(!(unitConflictsMap.hasOwnProperty('firstUnitIndex'))) {
                                unitConflictsMap['firstUnitIndex'] = unitIndex;
                                unitConflictsMap['firstUnitFirstLessonIndex'] = lessonIndex;
                            }
                            unitConflictsMap[unit['item-code']] = true;
                            lessonConflictsMap[lesson['item-code']] = true;
                            return true;
                        }
                        return false;
                    });
                });
            });
            showWarning = Object.keys(unitConflictsMap).length !== 0;
        }

        return { showWarning, unitConflictsMap, lessonConflictsMap };
    }

    // create map of existing rules
    createRulesMap(data, classStartDate, classEndDate, onlyLockRules = false) {
        const ruleMap: any = {};
        data.entities.forEach(rule => {
            if (rule.scope === "GLOBAL-ALL-USERS") {
                rule.entities.forEach(ruleEntity => {
                    if((onlyLockRules ? rule.rule_data_common.access === 'lock': rule.rule_data_common.access) && !this.isRuleEnded(rule.rule_data_common, classEndDate)) {
                        if(!ruleMap[ruleEntity.id]) ruleMap[ruleEntity.id] = [];
                        ruleMap[ruleEntity.id].push({
                            id: rule.ruleid,
                            access: rule.rule_data_common.access,
                            startDate: rule.rule_data_common.startTime ? rule.rule_data_common.startTime : classStartDate,
                            endDate: rule.rule_data_common.endTime ? rule.rule_data_common.endTime : classEndDate
                        });
                    }
                });
            }
        });
        return ruleMap;
    }

    /**
     * Checks if a rule has ended based on the current time and rule end date.
     * @param rule The rule object to check.
     * @param classEndDate The end date of the class.
     * @returns {boolean} True if the rule has ended, false otherwise.
     */
    isRuleEnded(ruleData: any, classEndDate: number) {
        // If the rule has a defined end time, use it as the end date. Otherwise, use the class end date.
        const endDate = ruleData.endTime ? ruleData.endTime : classEndDate;

        // Compare the current time with the end date to determine if the rule has ended.
        return this.currentTime > endDate;
    }

    collectRulesOnItem(ruleMap, tocArray, tocFolderDepth, productCode) {
        if (tocFolderDepth === 0) {
            tocArray.forEach(item => {
                if (!item.rulesList) item.rulesList = [];
                if(ruleMap[productCode]) {
                    item.rulesList.push(...ruleMap[productCode]);
                }
                if(ruleMap[item['item-code']]) {
                    item.rulesList.push(...ruleMap[item['item-code']]);
                }
                item.rulesList.sort((a, b) => a.startDate - b.startDate);
            });
        } else {
            tocArray.forEach(unit => {
                const unitLessons = this.productTocService.getLastFolderData(unit);
                unitLessons.forEach(lesson => {
                    lesson.items.forEach(item => {
                        if (!item.rulesList) item.rulesList = [];
                        if(ruleMap[productCode]) {
                            item.rulesList.push(...ruleMap[productCode]);
                        }
                        if(ruleMap[unit['item-code']]) {
                            item.rulesList.push(...ruleMap[unit['item-code']]);
                        }
                        if(ruleMap[lesson['item-code']]) {
                            item.rulesList.push(...ruleMap[lesson['item-code']]);
                        }
                        if(ruleMap[item['item-code']]) {
                            item.rulesList.push(...ruleMap[item['item-code']]);
                        }
                        item.rulesList.sort((a, b) => a.startDate - b.startDate);
                    });
                });
            });
        }
    }

    /**
     * Creates a default lock rule for an activity of an assignment-only product.
     * The lock rule end time is the minimum of the end times of the assignment-only lock rules for the activity 
     * and its parents(unit, lesson, etc).
     * @param itemCode - The item code of the activity
     * @param productCode - The product code of the activity
     * @returns - The default lock rule for the activity
     */
    createDefaultLockRule(itemCode, productCode) {
        let ruleEndTime = this.classEndDate;

        // Check the activity's lock rule
        if (this.lockRulesForAssignOnlyItems?.[itemCode]?.endTime < ruleEndTime) {
            ruleEndTime = this.lockRulesForAssignOnlyItems[itemCode].endTime;
        }

        // Check the product's lock rule
        if (this.lockRulesForAssignOnlyItems?.[productCode]?.endTime < ruleEndTime) {
            ruleEndTime = this.lockRulesForAssignOnlyItems[productCode].endTime;
        }

        // Check the parents' (unit, lesson) lock rules
        const parts = itemCode.split('/');
        let parentItemCode = parts[0];
        for (let i = 1; i < parts.length; i++) { 
            if (this.lockRulesForAssignOnlyItems?.[parentItemCode]?.endTime < ruleEndTime) {
                ruleEndTime = this.lockRulesForAssignOnlyItems[parentItemCode].endTime;
            }
            parentItemCode += '/' + parts[i];
        }

        return {
            startTime: this.classStartDate,
            endTime: ruleEndTime,
            access: 'lock'
        };
    }
    isRulesDocSizeValidForLearner(ruleCreationData, productData, productRules) {
        const rulesDoc = {
            global: {
                account: 'cup1',
                category: 'rule',
                rule_type: 'content-access',
                version: 2,
                modified: Date.now(),
                created: Date.now(),
                sk: "GLOBAL-ALL-USERS",
                rulesMeta: {}
            }
        };
        productData.classLearners.forEach(learner => {
            rulesDoc[learner.userid] = {
                account: 'cup1',
                category: 'rule',
                rule_type: 'content-access',
                version: 2,
                modified: Date.now(),
                created: Date.now(),
                sk: "USER",
                rulesMeta: {}
            };
        });
        productRules.entities.forEach((accessRule) => {
            if (accessRule.version === 1) {
                if (accessRule.scope === 'GLOBAL-ALL-USERS') {
                    rulesDoc.global.version = 1;
                    delete rulesDoc.global.rulesMeta
                } else {
                    accessRule.user.forEach(user => {
                        if (rulesDoc[user]) {
                            rulesDoc[user].version = 1;
                            delete rulesDoc[user].ruleMeta;
                        }
                    });
                }
            }
        });
        productRules.entities.forEach((accessRule) => {
            const {
                scope,
                rule_data_common,
                ruleid,
                entities: accessRuleEntities,
            } = accessRule;

            if (scope === "GLOBAL-ALL-USERS") {
                this.addingCommmonDataToRuleDoc(accessRule, rulesDoc.global);
                this.processRuleEntitiesForRuleDoc(accessRuleEntities, rulesDoc.global, rule_data_common, ruleid);
            } else {
                accessRule.user.forEach(user => {
                    // if the learner was removed from the class and a user scope rule exists for that learner, no need to check the size of the doc for that learner
                    if (!rulesDoc[user]) {
                        return;
                    }
                    this.addingCommmonDataToRuleDoc(accessRule, rulesDoc[user]);
                    this.processRuleEntitiesForRuleDoc(accessRuleEntities, rulesDoc[user], rule_data_common, ruleid);
                });
            }
        });

        const { rule_data_common, ruleid } = ruleCreationData;
        if (ruleCreationData.scope === "GLOBAL-ALL-USERS") {
            this.processRuleEntitiesForRuleDoc(ruleCreationData.entities, rulesDoc.global, rule_data_common, ruleid);
            return ((new TextEncoder().encode(JSON.stringify(rulesDoc.global))).length / 1024) < 390;
        } else {
            for (const user of ruleCreationData.user) {
                this.processRuleEntitiesForRuleDoc(ruleCreationData.entities, rulesDoc[user], rule_data_common, ruleid);
                if (((new TextEncoder().encode(JSON.stringify(rulesDoc[user]))).length / 1024) >= 390) return false;
            }
        }
        return true;
    }

    private addingCommmonDataToRuleDoc(accessRule: any, particularRulesDoc: any) {
        if (!particularRulesDoc.context) {
            const {
                context,
                orgid,
            } = accessRule;
            particularRulesDoc.context = context;
            particularRulesDoc.orgid = orgid;
            particularRulesDoc.pk = `RULE-${context}#content-access`;
        }
    }

    private processRuleEntitiesForRuleDoc(ruleEntityArray: any, particularRulesDoc: any, rule_data_common: any, ruleid: string) {
        ruleEntityArray.forEach((ruleEntity) => {
            const { id: entityId } = ruleEntity;

            particularRulesDoc[entityId] = particularRulesDoc[entityId] || {};
            if (particularRulesDoc.version === 2) {
                particularRulesDoc[entityId][ruleid] = {};
                particularRulesDoc.rulesMeta[ruleid] = {
                    ...rule_data_common
                };
            } else {
                particularRulesDoc[entityId][ruleid] = { ...rule_data_common };
            }
        });
    }

}